import React, { useState, useEffect, useMemo } from 'react';
import { useCrmSourceOptions, useCrmMyAccess, useCreateSourceOption } from '@/hooks/queries';

export default function ContactForm({ 
  contact = null, 
  onSubmit, 
  onCancel, 
  isSubmitting = false
}) {
  const { data: sourceOptionsData } = useCrmSourceOptions();
  const { data: myAccess } = useCrmMyAccess();
  const allSourceOptions = sourceOptionsData?.options || [];
  const createSourceMutation = useCreateSourceOption();
  
  // Filter source options for salespeople
  const sourceOptions = useMemo(() => {
    if (myAccess?.isAdmin) {
      return allSourceOptions;
    }
    // For salespeople, only show their allowed sources
    return allSourceOptions.filter(opt => myAccess?.allowedSources?.includes(opt.value));
  }, [allSourceOptions, myAccess]);

  const isAdmin = myAccess?.isAdmin;
  
  const [showAddSource, setShowAddSource] = useState(false);
  const [newSourceLabel, setNewSourceLabel] = useState('');
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    source: '',
    notes: '',
    nextAction: '',
    nextActionDue: '',
  });

  // Set default source for salespeople creating new contacts
  useEffect(() => {
    if (!contact && !isAdmin && sourceOptions.length > 0 && !formData.source) {
      setFormData(prev => ({ ...prev, source: sourceOptions[0].value }));
    }
  }, [contact, isAdmin, sourceOptions, formData.source]);

  useEffect(() => {
    if (contact) {
      setFormData({
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        email: contact.email || '',
        phone: contact.phone || '',
        company: contact.company || '',
        source: contact.source || '',
        notes: contact.notes || '',
        nextAction: contact.nextAction || '',
        nextActionDue: contact.nextActionDue ? new Date(contact.nextActionDue).toISOString().slice(0, 10) : '',
      });
    }
  }, [contact]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Ensure salespeople always have a source selected
    if (!isAdmin && !formData.source) {
      alert('Please select a source for this contact');
      return;
    }
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* First Name & Last Name - Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
            First Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
            placeholder="John"
          />
        </div>
        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
            Last Name
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
            placeholder="Smith"
          />
        </div>
      </div>

      {/* Email & Phone - Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
            placeholder="john@example.com"
          />
        </div>
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
            placeholder="+1 555 123 4567"
          />
        </div>
      </div>

      {/* Company */}
      <div>
        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
          Company
        </label>
        <input
          type="text"
          id="company"
          name="company"
          value={formData.company}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
          placeholder="Acme Corp"
        />
      </div>

      {/* Source */}
      <div>
        <label htmlFor="source" className="block text-sm font-medium text-gray-700 mb-1">
          Source {!isAdmin && <span className="text-red-500">*</span>}
        </label>
        <div className="flex gap-2">
          <select
            id="source"
            name="source"
            value={formData.source}
            onChange={handleChange}
            required={!isAdmin}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
          >
            {isAdmin && <option value="">Select source...</option>}
            {sourceOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.value}
              </option>
            ))}
          </select>
          {isAdmin && (
            <button
              type="button"
              onClick={() => setShowAddSource(true)}
              className="px-3 py-2 text-zapt-purple border border-zapt-purple rounded-lg hover:bg-zapt-purple/5"
              title="Add new source"
            >
              +
            </button>
          )}
        </div>
        {!isAdmin && sourceOptions.length === 1 && (
          <p className="text-xs text-gray-500 mt-1">
            Contacts will be assigned to your source: {sourceOptions[0].value}
          </p>
        )}
      </div>
      
      {/* Add Source Modal */}
      {showAddSource && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Source</h3>
            <input
              type="text"
              value={newSourceLabel}
              onChange={(e) => setNewSourceLabel(e.target.value)}
              placeholder="e.g., Trade Show, Partner Referral"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowAddSource(false);
                  setNewSourceLabel('');
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!newSourceLabel.trim() || createSourceMutation.isPending}
                onClick={() => {
                  createSourceMutation.mutate(
                    { value: newSourceLabel },
                    {
                      onSuccess: (newOption) => {
                        setFormData(prev => ({ ...prev, source: newOption.value }));
                        setShowAddSource(false);
                        setNewSourceLabel('');
                      },
                    }
                  );
                }}
                className="px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 disabled:opacity-50"
              >
                {createSourceMutation.isPending ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Next Action */}
      <div>
        <label htmlFor="nextAction" className="block text-sm font-medium text-gray-700 mb-1">
          Next Action
        </label>
        <input
          type="text"
          id="nextAction"
          name="nextAction"
          value={formData.nextAction}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
          placeholder="e.g., Schedule follow-up call"
        />
      </div>

      {/* Next Action Due Date */}
      <div>
        <label htmlFor="nextActionDue" className="block text-sm font-medium text-gray-700 mb-1">
          Next Action Due
        </label>
        <input
          type="date"
          id="nextActionDue"
          name="nextActionDue"
          value={formData.nextActionDue}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
        />
      </div>

      {/* Notes */}
      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
          Notes
        </label>
        <textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple"
          placeholder="Additional notes about this contact..."
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        {!isAdmin && sourceOptions.length === 0 && (
          <p className="text-sm text-amber-600 mr-auto self-center">
            Loading source options...
          </p>
        )}
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={isSubmitting}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isSubmitting || (!isAdmin && sourceOptions.length === 0)}
          className="px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 disabled:opacity-50"
        >
          {isSubmitting ? 'Saving...' : contact ? 'Update Contact' : 'Create Contact'}
        </button>
      </div>
    </form>
  );
}
